import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import styles from './chat-history-search.module.scss';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {InputControlItemType, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {InputField} from '@Components/input-field-v2';
import {HISTORY_FETCH_LIMIT} from '@/ai-chat/components/chat-history-list/chat-history-list.types';

export function ChatHistorySearch(): ReactElement {
  const {state, actions} = useAiChat();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (state.shouldFocusSearch) {
      inputRef.current?.focus();
      actions.setShouldFocusSearch(false);
    }
  }, [state.shouldFocusSearch]);

  const onSearchInput = (input: string): void => {
    if (input === '') {
      actions.setSearchQuery(undefined);
    } else {
      actions.setSearchQuery(input);
    }
  };

  const onSearchInputChangeEnded = (value: string): void => {
    const query = value.trim() === '' ? undefined : value.trim();

    actions.setSearchQuery(query);
    void actions.fetchChatHistory(query, 0, HISTORY_FETCH_LIMIT);
  };

  const onClearSearch = (): void => {
    actions.setSearchQuery(undefined);

    void actions.fetchChatHistory(undefined, 0, HISTORY_FETCH_LIMIT);
  };

  const getSearchBar = (): ReactElement => {
    return (
      <InputField
        ref={inputRef}
        input={state.searchQuery}
        onInputChange={onSearchInput}
        onInputChangeEnded={onSearchInputChangeEnded}
        placeholder={window.i18next.t('pmwjs_search_chats')}
        inputType={InputFieldType.SEARCH}
        inputClassName={styles.searchBoxBorder}
        className={styles.searchBox}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          className: styles.searchBoxIcon,
          customControls: [
            {
              type: InputControlItemType.ICONS_ONLY,
              id: 'search-icon',
              icon: state.searchQuery !== undefined ? 'icon-close' : 'icon-search',
            },
          ],
          onClick: onClearSearch,
        }}
      />
    );
  };

  return getSearchBar();
}
